import { DateValue } from '@internationalized/date';
import { TransportationAggregateType } from '@rematter/frontend-lib-api-client';
import { atom, useAtom } from 'jotai';

import { transportationOutboxEventCountsQueryAtom } from '../_graphql/queryTransportationOutboxEventCounts';
import { transportationOutboxEventsQueryAtom } from '../_graphql/queryTransportationOutboxEvents';

import {
  dateToZonedDateTimeEnd,
  dateToZonedDateTimeStart,
} from '~/shared/utils/dateTime';

export type TransportationAuditTab = 'events' | 'inbox';

export const $transportationAuditTab = atom<TransportationAuditTab>('events');

export const $transportationAggregateTypeFilter = atom<
  TransportationAggregateType | undefined
>(undefined);

export const $transportationAggregateIdFilter = atom<string | undefined>(
  undefined,
);

export const $transportationPagination = atom<{
  offset: number;
  limit: number;
}>({
  offset: 0,
  limit: 50,
});

export const $selectedTransportationEventId = atom<string | undefined>(
  undefined,
);

export const $transportationDateRangeFilter = atom<{
  start: DateValue;
  end: DateValue;
} | null>(null);

export const $remoteTransportationOutboxEvents =
  transportationOutboxEventsQueryAtom((get) => {
    const aggregateType = get($transportationAggregateTypeFilter);
    const aggregateId = get($transportationAggregateIdFilter);
    const pagination = get($transportationPagination);
    const dateRange = get($transportationDateRangeFilter);

    return {
      variables: {
        aggregateTypes: aggregateType ? [aggregateType] : undefined,
        aggregateId,
        pagination,
        createdAtRange:
          dateRange?.start && dateRange?.end
            ? {
                start: dateToZonedDateTimeStart(dateRange.start),
                end: dateToZonedDateTimeEnd(dateRange.end),
              }
            : undefined,
      },
    };
  });

export const $remoteTransportationOutboxEventCounts =
  transportationOutboxEventCountsQueryAtom();

export const useTransportationAuditTab = () => {
  const [tab, setTab] = useAtom($transportationAuditTab);
  return [tab, setTab] as const;
};

export const useTransportationAggregateTypeFilter = () => {
  const [filter, setFilter] = useAtom($transportationAggregateTypeFilter);
  const [, setAggregateId] = useAtom($transportationAggregateIdFilter);

  const setFilterAndResetId = (
    value: TransportationAggregateType | undefined,
  ) => {
    setFilter(value);
    setAggregateId(undefined);
  };

  return [filter, setFilterAndResetId] as const;
};

export const useTransportationAggregateIdFilter = () => {
  const [filter, setFilter] = useAtom($transportationAggregateIdFilter);
  return [filter, setFilter] as const;
};

export const useTransportationPagination = () => {
  const [pagination, setPagination] = useAtom($transportationPagination);
  return [pagination, setPagination] as const;
};

export const useSelectedTransportationEventId = () => {
  const [selectedId, setSelectedId] = useAtom($selectedTransportationEventId);
  return [selectedId, setSelectedId] as const;
};

export const useTransportationDateRangeFilter = () => {
  const [filter, setFilter] = useAtom($transportationDateRangeFilter);
  return [filter, setFilter] as const;
};
